// src/screens/PlatformAccountsScreen.js
// ============================================================================
// PLATFORM ACCOUNTS - Link Gaming Service Accounts (PSN, Xbox, Steam, etc.)
//
// Users claim their account names on each gaming service. These are linked
// to in-game names (IGNs) in game profiles. Uniqueness is enforced so only
// one CritCash user can hold a given name on a given service.
//
// Some games (like GT7) display both the platform account name and in-game
// name, so users need both tracked.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, TextInput, Modal, Alert, Linking
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import { PLATFORM_SERVICES } from '../constants/platformServices';

// Ordered list for display
const SERVICE_ORDER = [
  'psn', 'xbox', 'steam', 'epic', 'battlenet',
  'ea', 'riot', 'ubisoft', 'activision', 'nintendo', 'rockstar'
];

// ============================================================================
// ICON RENDERER
// ============================================================================
function ServiceIcon({ service, size = 24, color = '#FFF' }) {
  if (service.iconFamily === 'Ionicons') {
    return <Ionicons name={service.icon} size={size} color={color} />;
  }
  return <MaterialCommunityIcons name={service.icon} size={size} color={color} />;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function PlatformAccountsScreen({ navigation }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();

  const [accounts, setAccounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [selectedService, setSelectedService] = useState(null);
  const [accountNameInput, setAccountNameInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null); // null | 'available' | 'taken' | 'owned'

  // Dispute modal state
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeReason, setDisputeReason] = useState('false_claim');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [isFilingDispute, setIsFilingDispute] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  const fetchAccounts = useCallback(async () => {
    try {
      const getPlatformAccounts = httpsCallable(functions, 'getPlatformAccounts');
      const result = await getPlatformAccounts();
      setAccounts(result.data.accounts || {});
    } catch (error) {
      console.error('Error fetching platform accounts:', error);
      showError('Failed to load gaming accounts');
    } finally {
      setIsLoading(false);
    }
  }, [functions, showError]);

  useEffect(() => {
    if (isFocused) {
      fetchAccounts();
    }
  }, [isFocused, fetchAccounts]);

  // ============================================================================
  // AVAILABILITY CHECK
  // ============================================================================
  const checkAvailability = useCallback(async (serviceId, name) => {
    if (!name || name.trim().length < (PLATFORM_SERVICES[serviceId]?.minLength || 1)) {
      setAvailabilityStatus(null);
      return;
    }

    setIsCheckingAvailability(true);
    try {
      const check = httpsCallable(functions, 'checkPlatformAccountAvailability');
      const result = await check({ serviceId, accountName: name.trim() });

      if (result.data.available) {
        setAvailabilityStatus(result.data.ownedBySelf ? 'owned' : 'available');
      } else {
        setAvailabilityStatus('taken');
      }
    } catch (error) {
      console.error('Availability check error:', error);
      setAvailabilityStatus(null);
    } finally {
      setIsCheckingAvailability(false);
    }
  }, [functions]);

  // Debounced availability check
  useEffect(() => {
    if (!selectedService || !accountNameInput.trim()) {
      setAvailabilityStatus(null);
      return;
    }

    const timer = setTimeout(() => {
      checkAvailability(selectedService.id, accountNameInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [accountNameInput, selectedService, checkAvailability]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleOpenService = (service) => {
    const existing = accounts[service.id];
    setSelectedService(service);
    setAccountNameInput(existing?.accountName || '');
    setAvailabilityStatus(existing ? 'owned' : null);
  };

  const handleCloseModal = () => {
    setSelectedService(null);
    setAccountNameInput('');
    setAvailabilityStatus(null);
  };

  const handleSave = async () => {
    if (!selectedService) return;

    const trimmed = accountNameInput.trim();
    if (!trimmed) {
      Alert.alert('Invalid Name', `Please enter your ${selectedService.shortName} account name`);
      return;
    }

    if (trimmed.length < selectedService.minLength) {
      Alert.alert('Invalid Name', `${selectedService.shortName} name must be at least ${selectedService.minLength} character(s)`);
      return;
    }

    setIsSaving(true);
    try {
      const claimPlatformAccount = httpsCallable(functions, 'claimPlatformAccount');
      await claimPlatformAccount({
        serviceId: selectedService.id,
        accountName: trimmed,
      });

      // Update local state
      setAccounts(prev => ({
        ...prev,
        [selectedService.id]: {
          serviceId: selectedService.id,
          accountName: trimmed,
          accountNameLower: trimmed.toLowerCase(),
        }
      }));

      handleCloseModal();
      Alert.alert('Account Linked', `Your ${selectedService.shortName} account "${trimmed}" has been saved.`);
    } catch (error) {
      console.error('Error claiming platform account:', error);
      const msg = error.message || 'Failed to save account';

      if (msg.includes('already claimed')) {
        Alert.alert(
          'Name Taken',
          `This ${selectedService.shortName} name is already claimed by another CritCash user. You can file a dispute if this is your account.`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'File Dispute',
              onPress: () => {
                setShowDisputeModal(true);
              }
            }
          ]
        );
      } else {
        showError(msg);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedService) return;

    Alert.alert(
      'Remove Account',
      `Are you sure you want to unlink your ${selectedService.shortName} account?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            setIsSaving(true);
            try {
              const removePlatformAccount = httpsCallable(functions, 'removePlatformAccount');
              await removePlatformAccount({ serviceId: selectedService.id });

              setAccounts(prev => {
                const updated = { ...prev };
                delete updated[selectedService.id];
                return updated;
              });

              handleCloseModal();
            } catch (error) {
              console.error('Error removing platform account:', error);
              showError(error.message || 'Failed to remove account');
            } finally {
              setIsSaving(false);
            }
          }
        }
      ]
    );
  };

  const handleFileDispute = async () => {
    if (!selectedService || !accountNameInput.trim()) return;

    setIsFilingDispute(true);
    try {
      const filePlatformAccountDispute = httpsCallable(functions, 'filePlatformAccountDispute');
      await filePlatformAccountDispute({
        serviceId: selectedService.id,
        accountName: accountNameInput.trim(),
        reason: disputeReason,
        description: disputeDescription.trim(),
      });

      setShowDisputeModal(false);
      setDisputeReason('false_claim');
      setDisputeDescription('');
      handleCloseModal();

      Alert.alert(
        'Dispute Filed',
        'Your dispute has been submitted for admin review. We\'ll notify you when it\'s resolved.'
      );
    } catch (error) {
      console.error('Error filing dispute:', error);
      showError(error.message || 'Failed to file dispute');
    } finally {
      setIsFilingDispute(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
        <Text style={styles.loadingText}>Loading gaming accounts...</Text>
      </View>
    );
  }

  const linkedCount = Object.keys(accounts).length;

  return (
    <View style={styles.container}>
      <Header />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Back Button */}
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>{'← Back'}</Text>
        </TouchableOpacity>

        {/* Page Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Gaming Accounts</Text>
          <Text style={styles.subtitle}>
            Link your gaming platform accounts to pair with your in-game names
          </Text>
          <View style={styles.statsRow}>
            <Text style={styles.statsText}>
              {linkedCount} of {SERVICE_ORDER.length} accounts linked
            </Text>
          </View>
        </View>

        {/* Info Banner */}
        <View style={styles.infoBanner}>
          <Text style={styles.infoIcon}>{'ℹ️'}</Text>
          <Text style={styles.infoText}>
            Some games display both your platform account name and in-game name.
            Link your accounts here so opponents can verify your identity.
          </Text>
        </View>

        {/* Service Cards */}
        {SERVICE_ORDER.map(serviceId => {
          const service = PLATFORM_SERVICES[serviceId];
          if (!service) return null;

          const account = accounts[serviceId];
          const isLinked = !!account;

          return (
            <TouchableOpacity
              key={serviceId}
              style={[styles.serviceCard, isLinked && styles.serviceCardLinked]}
              onPress={() => handleOpenService(service)}
            >
              <View style={[styles.serviceIconContainer, { backgroundColor: service.color + '20' }]}>
                <ServiceIcon service={service} size={28} color={service.color === '#FFFFFF' ? '#CCC' : service.color} />
              </View>

              <View style={styles.serviceInfo}>
                <Text style={styles.serviceName}>{service.displayName}</Text>
                {isLinked ? (
                  <Text style={styles.accountNameText}>{account.accountName}</Text>
                ) : (
                  <Text style={styles.notLinkedText}>Tap to link</Text>
                )}
                <Text style={styles.platformsText}>
                  {service.hardwarePlatforms.join(' · ')}
                </Text>
              </View>

              <View style={styles.statusIndicator}>
                {isLinked ? (
                  <Text style={styles.checkmark}>{'✓'}</Text>
                ) : (
                  <Text style={styles.arrow}>{'→'}</Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}

        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Edit/Claim Modal */}
      <Modal
        visible={!!selectedService && !showDisputeModal}
        animationType="slide"
        transparent={true}
        onRequestClose={handleCloseModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              {selectedService && (
                <ServiceIcon
                  service={selectedService}
                  size={32}
                  color={selectedService.color === '#FFFFFF' ? '#CCC' : selectedService.color}
                />
              )}
              <View style={styles.modalTitleContainer}>
                <Text style={styles.modalTitle}>{selectedService?.displayName}</Text>
                <Text style={styles.modalSubtitle}>{selectedService?.accountNameLabel}</Text>
              </View>
              <TouchableOpacity onPress={handleCloseModal} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>{'✕'}</Text>
              </TouchableOpacity>
            </View>

            {/* Account Name Input */}
            <View style={styles.inputSection}>
              <Text style={styles.inputLabel}>{selectedService?.accountNameLabel}</Text>
              <TextInput
                style={styles.textInput}
                placeholder={selectedService?.accountNameHint || `Enter your ${selectedService?.shortName} name`}
                placeholderTextColor="#666"
                value={accountNameInput}
                onChangeText={setAccountNameInput}
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={selectedService?.maxLength || 32}
              />

              {/* Availability indicator */}
              <View style={styles.availabilityRow}>
                {isCheckingAvailability ? (
                  <View style={styles.availabilityIndicator}>
                    <ActivityIndicator size="small" color="#666" />
                    <Text style={styles.availabilityText}>Checking...</Text>
                  </View>
                ) : availabilityStatus === 'available' ? (
                  <View style={styles.availabilityIndicator}>
                    <Text style={styles.availableIcon}>{'✓'}</Text>
                    <Text style={[styles.availabilityText, styles.availableText]}>Available</Text>
                  </View>
                ) : availabilityStatus === 'taken' ? (
                  <View style={styles.availabilityIndicator}>
                    <Text style={styles.takenIcon}>{'✕'}</Text>
                    <Text style={[styles.availabilityText, styles.takenText]}>Taken by another user</Text>
                  </View>
                ) : availabilityStatus === 'owned' ? (
                  <View style={styles.availabilityIndicator}>
                    <Text style={styles.availableIcon}>{'✓'}</Text>
                    <Text style={[styles.availabilityText, styles.availableText]}>Your current account</Text>
                  </View>
                ) : null}
              </View>

              {/* Dispute link + support email when taken */}
              {availabilityStatus === 'taken' && (
                <View style={styles.takenActions}>
                  <TouchableOpacity
                    style={styles.disputeLink}
                    onPress={() => setShowDisputeModal(true)}
                  >
                    <Text style={styles.disputeLinkText}>
                      Is this your account? File a dispute
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.supportLink}
                    onPress={() => {
                      const subject = `Gaming Account Claim Help — ${selectedService?.shortName} "${accountNameInput.trim()}"`;
                      const body = `Hi CritCash Support,\n\nI'm trying to link my ${selectedService?.displayName} account but the name "${accountNameInput.trim()}" is already claimed by another CritCash user. This account belongs to me.\n\nPlease help me recover this claim.\n\nThanks.`;
                      const mailto = `mailto:support@critcash.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                      Linking.openURL(mailto).catch(() => {
                        showError('Could not open email client. Please email support@critcash.com directly.');
                      });
                    }}
                  >
                    <Text style={styles.supportLinkText}>
                      Or email support@critcash.com for help
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              <Text style={styles.inputHint}>
                This should match your account name on {selectedService?.displayName}
              </Text>
            </View>

            {/* Hardware platforms this service covers */}
            <View style={styles.platformBadgesSection}>
              <Text style={styles.platformBadgesLabel}>Covers platforms:</Text>
              <View style={styles.platformBadges}>
                {selectedService?.hardwarePlatforms.map(p => (
                  <View key={p} style={styles.platformBadge}>
                    <Text style={styles.platformBadgeText}>{p}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Action Buttons */}
            <View style={styles.modalActions}>
              {accounts[selectedService?.id] && (
                <TouchableOpacity
                  style={styles.deleteButton}
                  onPress={handleRemove}
                  disabled={isSaving}
                >
                  <Text style={styles.deleteButtonText}>Unlink</Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[
                  styles.saveButton,
                  (!accountNameInput.trim() || isSaving || availabilityStatus === 'taken') && styles.saveButtonDisabled
                ]}
                onPress={handleSave}
                disabled={!accountNameInput.trim() || isSaving || availabilityStatus === 'taken'}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <Text style={styles.saveButtonText}>
                    {accounts[selectedService?.id] ? 'Update' : 'Link Account'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Dispute Modal */}
      <Modal
        visible={showDisputeModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowDisputeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <View style={styles.modalTitleContainer}>
                <Text style={styles.modalTitle}>File a Dispute</Text>
                <Text style={styles.modalSubtitle}>
                  {selectedService?.shortName}: {accountNameInput}
                </Text>
              </View>
              <TouchableOpacity onPress={() => setShowDisputeModal(false)} style={styles.closeButton}>
                <Text style={styles.closeButtonText}>{'✕'}</Text>
              </TouchableOpacity>
            </View>

            {/* Reason Selection */}
            <View style={styles.inputSection}>
              <Text style={styles.inputLabel}>Reason</Text>
              {[
                { value: 'false_claim', label: 'Someone falsely claimed my account' },
                { value: 'account_compromised', label: 'My account was compromised' },
                { value: 'name_change', label: 'I changed my name, old claim not released' },
                { value: 'other', label: 'Other' },
              ].map(option => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.reasonOption,
                    disputeReason === option.value && styles.reasonOptionSelected
                  ]}
                  onPress={() => setDisputeReason(option.value)}
                >
                  <View style={[
                    styles.radioOuter,
                    disputeReason === option.value && styles.radioOuterSelected
                  ]}>
                    {disputeReason === option.value && <View style={styles.radioInner} />}
                  </View>
                  <Text style={[
                    styles.reasonOptionText,
                    disputeReason === option.value && styles.reasonOptionTextSelected
                  ]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Description */}
            <View style={styles.inputSection}>
              <Text style={styles.inputLabel}>Description (optional)</Text>
              <TextInput
                style={[styles.textInput, styles.textArea]}
                placeholder="Provide any details that can help us verify your claim..."
                placeholderTextColor="#666"
                value={disputeDescription}
                onChangeText={setDisputeDescription}
                multiline
                numberOfLines={4}
                maxLength={500}
              />
            </View>

            {/* Submit */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowDisputeModal(false)}
                disabled={isFilingDispute}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.saveButton, isFilingDispute && styles.saveButtonDisabled]}
                onPress={handleFileDispute}
                disabled={isFilingDispute}
              >
                {isFilingDispute ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <Text style={styles.saveButtonText}>Submit Dispute</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <NavBar navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0D0D12',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },

  // Back Button
  backButton: {
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
    backgroundColor: '#1A1A24',
  },
  backButtonText: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '700',
  },

  // Loading
  loadingContainer: {
    flex: 1,
    backgroundColor: '#0D0D12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Header
  header: {
    marginBottom: 20,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 8,
  },
  subtitle: {
    color: '#888',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statsText: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '600',
  },

  // Info Banner
  infoBanner: {
    flexDirection: 'row',
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#2A2A36',
    alignItems: 'flex-start',
  },
  infoIcon: {
    fontSize: 16,
    marginRight: 10,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    color: '#AAA',
    fontSize: 13,
    lineHeight: 19,
  },

  // Service Cards
  serviceCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  serviceCardLinked: {
    borderColor: '#FCD000',
    borderWidth: 1,
  },
  serviceIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 2,
  },
  accountNameText: {
    color: '#FCD000',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  notLinkedText: {
    color: '#666',
    fontSize: 13,
    fontStyle: 'italic',
    marginBottom: 2,
  },
  platformsText: {
    color: '#555',
    fontSize: 11,
    marginTop: 2,
  },
  statusIndicator: {
    width: 30,
    alignItems: 'center',
  },
  checkmark: {
    color: '#2ECC71',
    fontSize: 20,
    fontWeight: '700',
  },
  arrow: {
    color: '#555',
    fontSize: 18,
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1A1A24',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 480,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  modalTitleContainer: {
    flex: 1,
    marginLeft: 12,
  },
  modalTitle: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: '800',
  },
  modalSubtitle: {
    color: '#888',
    fontSize: 13,
    marginTop: 2,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#2A2A36',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: '#888',
    fontSize: 16,
    fontWeight: '700',
  },

  // Input
  inputSection: {
    marginBottom: 20,
  },
  inputLabel: {
    color: '#CCC',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#0D0D12',
    borderRadius: 10,
    padding: 14,
    color: '#FFF',
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  inputHint: {
    color: '#666',
    fontSize: 12,
    marginTop: 6,
  },

  // Availability
  availabilityRow: {
    marginTop: 8,
    minHeight: 20,
  },
  availabilityIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  availabilityText: {
    color: '#888',
    fontSize: 13,
    marginLeft: 6,
  },
  availableText: {
    color: '#2ECC71',
  },
  takenText: {
    color: '#E74C3C',
  },
  availableIcon: {
    color: '#2ECC71',
    fontSize: 14,
    fontWeight: '700',
  },
  takenIcon: {
    color: '#E74C3C',
    fontSize: 14,
    fontWeight: '700',
  },

  // Dispute + support links (shown when account name is taken)
  takenActions: {
    marginTop: 8,
    gap: 6,
  },
  disputeLink: {},
  disputeLinkText: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  supportLink: {},
  supportLinkText: {
    color: '#9BA3AF',
    fontSize: 12,
    fontWeight: '500',
    textDecorationLine: 'underline',
  },

  // Platform badges
  platformBadgesSection: {
    marginBottom: 20,
  },
  platformBadgesLabel: {
    color: '#888',
    fontSize: 12,
    marginBottom: 8,
  },
  platformBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  platformBadge: {
    backgroundColor: '#2A2A36',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  platformBadgeText: {
    color: '#AAA',
    fontSize: 12,
    fontWeight: '500',
  },

  // Dispute reason options
  reasonOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 6,
    backgroundColor: '#0D0D12',
  },
  reasonOptionSelected: {
    backgroundColor: '#2A2A36',
    borderWidth: 1,
    borderColor: '#FCD000',
  },
  reasonOptionText: {
    color: '#AAA',
    fontSize: 14,
    marginLeft: 10,
  },
  reasonOptionTextSelected: {
    color: '#FFF',
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: '#555',
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioOuterSelected: {
    borderColor: '#FCD000',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#FCD000',
  },

  // Modal Actions
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#FCD000',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 24,
    minWidth: 120,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '700',
  },
  deleteButton: {
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: '#E74C3C',
    alignItems: 'center',
  },
  deleteButtonText: {
    color: '#E74C3C',
    fontSize: 15,
    fontWeight: '600',
  },
  cancelButton: {
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: '#555',
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#AAA',
    fontSize: 15,
    fontWeight: '600',
  },
});
